import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { Category, Transaction } from '../db/database'
import {
  allocationsForEnvelope, envelopeHistory, planDays, type Envelope,
} from '../db/budget'
import { EnvelopeDays } from './EnvelopeHistory'
import { daysOf, splitEvenly, type CycleRange } from '../lib/cycles'
import { formatAmount, formatAriary, parseAmountBounded } from '../lib/money'
import { formatDay, formatShortDate, today, weekdayName } from '../lib/dates'
import { Icon } from './Icon'
import './PlanDaysSheet.css'
import { MAX_NOM } from '../lib/limites'

/**
 * Prévoir une enveloppe jour par jour.
 *
 * Deux façons de poser les montants, parce que les deux existent dans la vraie
 * vie : « je mets 20 000 par jour » (on saisit le montant) et « je répartis mes
 * 100 000 sur le mois » (l'app divise). Dans les deux cas chaque journée reste
 * modifiable à la main — un mardi de fête n'est pas un mardi ordinaire.
 *
 * La division n'égare jamais un ariary : `splitEvenly` fait absorber le reste
 * par le dernier jour. Sans ça, 100 000 sur 30 jours en afficherait 99 990, et
 * une app d'argent qui perd de l'argent à l'écran perd sa crédibilité.
 */

/** 0 = lundi, comme les cycles hebdomadaires. */
const LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
const WEEK = [0, 1, 2, 3, 4, 5, 6]
const WEEKDAYS = [0, 1, 2, 3, 4]
const WEEKEND = [5, 6]

function weekdayOf(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  return (new Date(y, m - 1, d).getDay() + 6) % 7
}

export function PlanDaysSheet(
  { walletId, category, envelope, range, onClose, onOpen }: {
    walletId: string
    category: Category
    envelope: Envelope
    range: CycleRange
    onClose: () => void
    /**
     * Ouvrir le détail d'une dépense déjà passée. Absent = l'historique reste
     * en lecture seule, ce qui est le comportement sûr si un écran l'oublie.
     */
    onOpen?: (transaction: Transaction) => void
  },
) {
  const [weekdays, setWeekdays] = useState<number[]>(WEEK)
  const [mode, setMode] = useState<'split' | 'perDay'>('split')
  const [perDay, setPerDay] = useState('')
  const [label, setLabel] = useState('')
  const [amounts, setAmounts] = useState<Record<string, number>>({})
  const [locked, setLocked] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState(false)

  // On ne planifie pas le passé : les jours déjà écoulés du cycle ne peuvent
  // plus être prévus, seulement constatés.
  const openDays = useMemo(() => {
    const from = today() > range.start ? today() : range.start
    return daysOf(range).filter((day) => day >= from)
  }, [range])

  /*
   * Une journée DÉJÀ CONFIRMÉE n'est plus planifiable.
   *
   * Sans ce filtre, le jour même apparaissait DEUX FOIS : une fois en haut,
   * avec un champ à 0 — parce que son montant vit dans `locked` et non dans
   * `amounts` — et une fois en bas, en « dépensé ». Le compte de journées
   * s'en trouvait faux lui aussi : « 8 jours » pour 7 journées réellement
   * prévues. Ce qui est dépensé se constate, il ne se prévoit plus.
   */
  const selected = useMemo(
    () => openDays.filter((day) =>
      weekdays.includes(weekdayOf(day)) && locked[day] === undefined),
    [openDays, weekdays, locked],
  )

  /*
   * Ce qu'il reste vraiment à répartir.
   *
   * `remaining` et non `amount` : les journées déjà confirmées et les dépenses
   * saisies à la main ont retiré leur part de l'enveloppe. Répartir le montant
   * PLEIN sur les jours qui restent reproposerait un argent déjà dépensé, et
   * le plan dépasserait l'enveloppe sans que rien ne le dise.
   */
  const restant = Math.max(0, envelope.remaining)

  /** Répartit ce qui reste sur des jours donnés, le dernier absorbant le reste. */
  function evenly(days: string[]): Record<string, number> {
    if (days.length === 0) return {}
    const parts = splitEvenly(restant, days.length)
    return Object.fromEntries(days.map((day, index) => [day, parts[index]]))
  }

  // Reprend le plan déjà posé, s'il y en a un : rouvrir l'écran ne doit pas
  // faire table rase de ce que l'utilisateur avait prévu. S'il n'y en a pas, on
  // PROPOSE d'emblée la répartition de l'enveloppe sur les jours restants —
  // ouvrir l'écran sur une colonne de zéros n'aiderait personne, et obligerait
  // à toucher un réglage avant de voir quoi que ce soit.
  useEffect(() => {
    void allocationsForEnvelope(walletId, category.id, range).then((rows) => {
      const existing: Record<string, number> = {}
      const done: Record<string, number> = {}
      for (const row of rows) {
        if (row.confirmed_at) done[row.planned_date] = row.planned_amount
        else existing[row.planned_date] = row.planned_amount
      }
      setLocked(done)

      if (Object.keys(existing).length === 0) {
        setAmounts(evenly(openDays.filter((day) =>
          WEEK.includes(weekdayOf(day)) && done[day] === undefined)))
        return
      }
      setAmounts(existing)
      setWeekdays([...new Set(Object.keys(existing).map(weekdayOf))].sort())
    })
    // `openDays` et le montant de l'enveloppe suffisent : le reste est du state
    // local que cet effet initialise, il ne doit pas le relire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletId, category.id, range, openDays, envelope.amount, envelope.remaining])

  /** Repose tous les montants. Appelé quand le rythme ou le mode change. */
  function spread(days: string[], nextMode: 'split' | 'perDay', unit: string) {
    if (days.length === 0) return setAmounts({})
    if (nextMode === 'perDay') {
      const value = parseAmountBounded(unit) ?? 0
      return setAmounts(Object.fromEntries(days.map((day) => [day, value])))
    }
    setAmounts(evenly(days))
  }

  function chooseRhythm(next: number[]) {
    setWeekdays(next)
    spread(openDays.filter((day) => next.includes(weekdayOf(day))), mode, perDay)
  }

  function toggleWeekday(index: number) {
    const next = weekdays.includes(index)
      ? weekdays.filter((d) => d !== index)
      : [...weekdays, index].sort()
    chooseRhythm(next)
  }

  const planned = selected.reduce((sum, day) => sum + (amounts[day] ?? 0), 0)
  // Le dépassement se juge sur ce qui RESTE, pas sur l'enveloppe pleine :
  // sinon on laisserait replanifier un argent déjà sorti.
  const over = planned > restant
  const dejaSorti = envelope.spent > 0

  /*
   * L'historique de l'enveloppe, en direct.
   *
   * `useLiveQuery` et non l'effet du dessus : supprimer une de ces dépenses
   * depuis le détail doit la faire disparaître d'ici sans refermer la feuille.
   * Et on affiche les TRANSACTIONS, pas le montant de la journée prévue — une
   * journée en porte souvent plusieurs, et n'en montrer qu'une revenait à
   * cacher tout ce qui a été ajouté au fil du marché.
   */
  const spentDays = useLiveQuery(
    () => envelopeHistory(walletId, category.id, range),
    [walletId, category.id, range], [],
  )

  async function save() {
    setSaving(true)
    await planDays({
      walletId,
      categoryId: category.id,
      range,
      days: selected.map((date) => ({ date, amount: amounts[date] ?? 0 })),
      label: label.trim() || null,
    })
    onClose()
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={`Prévoir ${category.name} jour par jour`}>
      <div className="sheet plan-sheet">
        <header className="sheet-head">
          <h2>Prévoir · {category.name}</h2>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Fermer">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="plan-body">
          <p className="plan-envelope">
            Enveloppe : <strong className="tabular">{formatAriary(envelope.amount)}</strong>
            {/* Ce qui reste, dès qu'un ariary en est sorti : c'est sur CE
                nombre que le plan se construit, pas sur l'enveloppe pleine. */}
            {dejaSorti && (
              <>
                {' · '}reste <strong className="tabular">{formatAriary(restant)}</strong>
                {' '}à répartir
              </>
            )}
          </p>

          <fieldset className="plan-group">
            <legend>Quels jours ?</legend>
            <div className="plan-choices">
              <button type="button" onClick={() => chooseRhythm(WEEK)}
                      aria-pressed={weekdays.length === 7}>Tous les jours</button>
              <button type="button" onClick={() => chooseRhythm(WEEKDAYS)}
                      aria-pressed={weekdays.join() === WEEKDAYS.join()}>Lundi à vendredi</button>
              <button type="button" onClick={() => chooseRhythm(WEEKEND)}
                      aria-pressed={weekdays.join() === WEEKEND.join()}>Samedi et dimanche</button>
            </div>
            <div className="plan-weekdays">
              {LABELS.map((letter, index) => (
                <button
                  key={index}
                  type="button"
                  className={weekdays.includes(index) ? 'is-on' : ''}
                  aria-pressed={weekdays.includes(index)}
                  aria-label={['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi',
                               'Samedi', 'Dimanche'][index]}
                  onClick={() => toggleWeekday(index)}
                >{letter}</button>
              ))}
            </div>
          </fieldset>

          <fieldset className="plan-group">
            <legend>Combien ?</legend>
            <div className="plan-choices">
              <button type="button" aria-pressed={mode === 'split'}
                      onClick={() => { setMode('split'); spread(selected, 'split', perDay) }}>
                Répartir l'enveloppe
              </button>
              <button type="button" aria-pressed={mode === 'perDay'}
                      onClick={() => { setMode('perDay'); spread(selected, 'perDay', perDay) }}>
                Montant par jour
              </button>
            </div>
            {mode === 'perDay' && (
              <label className="plan-perday">
                <input
                  type="text" inputMode="numeric" className="tabular"
                  value={perDay} placeholder="0"
                  aria-label="Montant par jour"
                  onChange={(event) => {
                    setPerDay(event.target.value)
                    spread(selected, 'perDay', event.target.value)
                  }}
                />
                <span>Ar par jour</span>
              </label>
            )}
          </fieldset>

          <label className="plan-label">
            <span>Pour quoi ? (facultatif)</span>
            <input type="text" maxLength={MAX_NOM} value={label} placeholder="riz, viande…"
                   onChange={(event) => setLabel(event.target.value)} />
          </label>

          <p className={`plan-total ${over ? 'is-over' : ''}`}>
            {selected.length} jour{selected.length > 1 ? 's' : ''} ·{' '}
            <strong className="tabular">{formatAriary(planned)}</strong> prévus
            {/* L'écart se mesure sur ce qui RESTE, comme le dépassement
                lui-même : le comparer à l'enveloppe pleine affichait un écart
                négatif dès qu'une journée avait été confirmée. */}
            {over && ` — ${formatAriary(planned - restant)} de plus qu'il n'en reste`}
          </p>

          <ul className="plan-days">
            {selected.map((day) => (
              <li key={day}>
                <span className="plan-date">
                  <b>{day === today() ? "Aujourd'hui" : weekdayName(day)}</b>{' '}
                  {formatShortDate(day)}
                </span>
                <span className="plan-field">
                  <input
                    type="text" inputMode="numeric" className="tabular"
                    aria-label={`Montant du ${formatDay(day)}`}
                    value={formatAmount(amounts[day] ?? 0)}
                    onChange={(event) => setAmounts((current) => ({
                      ...current, [day]: parseAmountBounded(event.target.value) ?? 0,
                    }))}
                  />
                  <span aria-hidden="true">Ar</span>
                </span>
              </li>
            ))}
          </ul>

          {/* HORS de la liste des jours à prévoir : ce qui est dépensé ne se
              prévoit plus, et l'imbriquer dans la même liste le faisait passer
              pour un doublon de la ligne du jour. */}
          {spentDays.length > 0 && (
            <section className="plan-spent-block">
              <h3>Déjà dépensé</h3>
              <EnvelopeDays days={spentDays} category={category} onOpen={onOpen} />
            </section>
          )}
        </div>

        <button type="button" className="plan-save" disabled={saving || selected.length === 0}
                onClick={() => void save()}>
          Enregistrer le plan
        </button>
      </div>
    </div>
  )
}
